import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reapproService, fournisseurService, ReapproSuggestion } from '../../api/services';
import { Fournisseur } from '../../types';

const Reappro: React.FC = () => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<ReapproSuggestion[]>([]);
  const [suppliers, setSuppliers] = useState<Fournisseur[]>([]);
  const [useAi, setUseAi] = useState(false);
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState<Record<number, number>>({}); // articleId -> qty
  const [fournisseurId, setFournisseurId] = useState<number | ''>('');
  const [generating, setGenerating] = useState(false);

  const loadSuggestions = (ai: boolean) => {
    setBusy(true);
    reapproService.suggestions(ai)
      .then((s) => {
        setSuggestions(s);
        setSelected(Object.fromEntries(s.map((x) => [x.articleId, x.suggestedQty])));
      })
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    fournisseurService.getAll({ size: 1000 }).then((f) => {
      setSuppliers(f.content.filter((x) => x.actif !== false));
    });
    loadSuggestions(false);
  }, []);

  const toggleAi = () => {
    const next = !useAi;
    setUseAi(next);
    loadSuggestions(next);
  };

  const toggleRow = (articleId: number, suggestedQty: number) => {
    setSelected((prev) => {
      const copy = { ...prev };
      if (articleId in copy) delete copy[articleId];
      else copy[articleId] = suggestedQty;
      return copy;
    });
  };

  const selectedLines = () =>
    Object.entries(selected)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([articleId, qty]) => ({ articleId: Number(articleId), quantite: Number(qty) }));

  const generateAuto = async () => {
    const lignes = selectedLines();
    if (lignes.length === 0) { toast.error('Sélectionnez au moins une ligne'); return; }
    setGenerating(true);
    try {
      const res = await reapproService.generateBonsCommandeAuto(lignes);
      const n = res.bonsCommande?.length ?? 0;
      const orphans = res.articlesSansFournisseur?.length ?? 0;
      toast.success(`${n} bon(s) de commande généré(s)`
        + (orphans ? ` · ${orphans} article(s) sans fournisseur préféré` : ''));
      if (n > 0) navigate('/documents/bons-commande');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const generate = async () => {
    if (fournisseurId === '') { toast.error('Choisissez un fournisseur'); return; }
    const lignes = Object.entries(selected)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([articleId, qty]) => ({ articleId: Number(articleId), quantite: Number(qty) }));
    if (lignes.length === 0) { toast.error('Sélectionnez au moins une ligne'); return; }

    setGenerating(true);
    try {
      await reapproService.generateBonCommande({ fournisseurId: Number(fournisseurId), lignes });
      toast.success('Bon de commande généré');
      navigate('/documents/bons-commande');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const selectedCount = Object.keys(selected).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Réapprovisionnement intelligent</h1>
          <p className="text-sm text-gray-500">
            Suggestions basées sur la consommation, le délai d’appro et le stock de sécurité.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={useAi} onChange={toggleAi} />
          Affiner avec l’IA
        </label>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {busy ? (
          <p className="p-4 text-sm text-gray-500">Calcul des suggestions…</p>
        ) : suggestions.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">Aucun réapprovisionnement nécessaire 🎉</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-3"></th>
                <th className="px-3 py-3 text-left">Article</th>
                <th className="px-3 py-3 text-right">Stock</th>
                <th className="px-3 py-3 text-right">Conso/j</th>
                <th className="px-3 py-3 text-right">Pt commande</th>
                <th className="px-3 py-3 text-right">À commander</th>
                <th className="px-3 py-3 text-left">Fournisseur</th>
                <th className="px-3 py-3 text-left">Justification</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm">
              {suggestions.map((s) => {
                const checked = s.articleId in selected;
                return (
                  <tr key={s.articleId} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={checked}
                        onChange={() => toggleRow(s.articleId, s.suggestedQty)} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{s.reference}</div>
                      <div className="text-xs text-gray-500">{s.designation}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{s.stockActuel}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{s.avgDailyDemand.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{s.reorderPoint.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number" min="0" step="1"
                        value={checked ? selected[s.articleId] : s.suggestedQty}
                        disabled={!checked}
                        onChange={(e) => setSelected((p) => ({ ...p, [s.articleId]: Number(e.target.value) }))}
                        className="w-20 rounded-lg border-gray-300 text-sm text-right disabled:bg-gray-50"
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {s.fournisseurNom || <span className="text-amber-600">À définir</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {s.source === 'AI' && (
                        <span className="mr-1 rounded-full bg-indigo-100 text-indigo-800 px-1.5 py-0.5 text-[10px] font-semibold">IA</span>
                      )}
                      {s.rationale}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-gray-600">
              Générer automatiquement un bon de commande par <strong>fournisseur préféré</strong>
              (défini dans la fiche article).
            </p>
            <button
              onClick={generateAuto}
              disabled={generating || selectedCount === 0}
              className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {generating ? 'Génération…' : `Générer auto par fournisseur (${selectedCount})`}
            </button>
          </div>
          <div className="border-t border-gray-100" />
          <div className="flex flex-wrap items-end gap-4">
          <label className="block text-sm">
            <span className="text-gray-700">Fournisseur du bon de commande</span>
            <select
              value={fournisseurId}
              onChange={(e) => setFournisseurId(e.target.value ? Number(e.target.value) : '')}
              className="mt-1 block w-64 rounded-lg border-gray-300 text-sm"
            >
              <option value="">— Sélectionner —</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.raisonSociale}</option>)}
            </select>
          </label>
          <button
            onClick={generate}
            disabled={generating || selectedCount === 0}
            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {generating ? 'Génération…' : `Générer le bon de commande (${selectedCount})`}
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reappro;
